import { httpRequest } from '../../shared/lib/utils/httpClient';
import { rowsPerPage } from '../../pages/constants';
import { handleError } from './FacilityUserService';

export interface FacilityList {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  facilityList: Facility[];
}

export interface Facility {
  id: number;
  facilityBusinessId: string | null;
  facilityName: string | null;
  partnerName: string | null;
  createdOn: string | null;
  serviceProvider: string | null;
  utilizeScribes: boolean | null;
  status: string | null;
  clinicianCount: number;
}

export interface ServicesProvided {
  id: number;
  name: string;
}

export interface FacilityType {
  id: number;
  name: string;
}

export interface VideoConferenceType {
  id: number;
  name: string;
}

export interface IntakeForm {
  id: number;
  name: string;
}

export interface PatientRecordAccess {
  id: number;
  name: string;
}

export interface AssignedTo {
  id: number;
  name: string;
}

export interface PartnerValue {
  name: string;
  type: string;
  businessId: string;
  status: string;
}

export interface Partner {
  id: number | string;
  value: PartnerValue;
}

export interface FacilityMetaData {
  servicesProvided: ServicesProvided[];
  facilityTypes: FacilityType[];
  videoConferenceTypeId: VideoConferenceType[];
  intakeForms: IntakeForm[];
  patientRecordAccess: PatientRecordAccess[];
  assignedTo: AssignedTo[];
  usStates: string[];
  partners: Partner[];
}

interface FacilityInfo {
  id: number;
  name: string;
  servicesProvided: ServicesProvided[];
  alias: string;
  status: boolean;
  facilityBusinessId: string;
  facilityTypeId: number;
  partnerId: number;
  shortName: string;
  website: string;
  utilizeScribes: boolean;
  activeFacilitySince: string;
  intakeFormId: number;
  terminationDate: string;
  assignedTo: number;
  videoConferenceTypeId: number;
  customLink: string;
  isCustomLink: boolean;
  minimumAgeRequired: string;
  isHidePhiAfterCompletion: boolean;
}

interface ContactDetails {
  id: number;
  primaryPhone: string;
  itHelpDeskEmail: string;
  itHelpDeskPhone: string;

  edPhone: string;
  icuPhone: string;
  medSurgPhone: string;
  otherFax: string;
  otherFaxDescription: string;
  secureFax: string;
  other: string;
}

interface ITSystemDetails {
  id: number;
  networkAccessType: string;
  telemedicinePlatform: string;
  networkAccessUrl: string;
  telemedicinePlatformUrl: string;
  vpnGatewaySoftware: string;
  emr: string;
  documentationIn: string;
  mfa: string;
  devicesUtilized: string;
  itNotes: string;
  qlerViewUsername: string;
  epicContext: string;
  trainingRequirements: string;
  portalUnPw: string;
}

export interface Address {
  id: number;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  county: string;
}

export interface BillingAddress {
  id: number;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCounty: string;
}

export interface FacilityData {
  facilityInfo: FacilityInfo;
  contactDetails: ContactDetails;
  itSystemDetails: ITSystemDetails;
  address: Address;
  description: string;
}

export type EmailCheckResult = {
  isAvailable: boolean;
  message?: string;
};
type GetFacilityListParams = Readonly<{
  partnerId?: number;
  clinicianId?: number;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
}>;

export const getFacilityList = async (
  options: GetFacilityListParams = {}
): Promise<FacilityList> => {
  const {
    partnerId,
    clinicianId,
    page = 0,
    size = rowsPerPage,
    sortField,
    sortType,
    searchTerm,
    statusId,
  } = options;

  const params: Record<string, string | number> = {
    page,
    size,
  };

  const userId = localStorage.getItem('userId') ?? '0';

  if (partnerId) params.partnerId = partnerId;
  if (clinicianId) params.clinicianId = clinicianId;
  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId?.length) params.statusId = statusId.join(',');

  const response = await httpRequest<FacilityList>({
    method: 'GET',
    url: '/operation/facilities',
    params,
    headers: { userId },
  });

  return response;
};

export const getFacilityMetaData = async (
  userId: string
): Promise<FacilityMetaData> => {
  const response = await httpRequest<FacilityMetaData>({
    method: 'GET',
    url: `/operation/facility/metadata`,
    headers: { userId },
  });
  return response;
};

export const saveOrUpdateFacility = async (
  userId: string,
  action: string,
  payload: FacilityData
) => {
  const apiAction = action === 'Save' ? 'SAVE' : 'UPDATE';
  const response = await httpRequest<FacilityData>({
    method: 'PUT',
    url: `/operation/facility?action=${apiAction}`,
    headers: { userId },
    data: payload,
  });
  return response;
};

export const getFacilityDetailsById = async (
  userId: string,
  facilityId: number
): Promise<FacilityData> => {
  const response = await httpRequest<FacilityData>({
    method: 'GET',
    url: `/operation/facility?facilityId=${facilityId}`,
    headers: { userId },
  });
  return response;
};

export const getFacilityClinicianMappedList = async (
  clinicianId?: number,
  page: number = 0,
  size: number = rowsPerPage,
  sortField?: string,
  sortType?: string,
  searchTerm?: string,
  statusFilter?: string
): Promise<FacilityList> => {
  const params: Record<string, string | number> = {
    page,
    size,
  };
  const userId = localStorage.getItem('userId') ?? '0';

  if (clinicianId) params.clinicianId = clinicianId;
  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusFilter) params.statusFilter = statusFilter;

  const response = await httpRequest<FacilityList>({
    method: 'GET',
    url: '/operation/facilities',
    params,
    headers: { userId },
  });
  return response;
};

export const checkShotNameUniq = async (
  shortName: string,
  facilityId?: number
): Promise<EmailCheckResult> => {
  const params = new URLSearchParams({
    shortName,
    ...(facilityId !== undefined && {
      facilityId: facilityId.toString(),
    }),
  });

  try {
    const response = await httpRequest<EmailCheckResult>({
      method: 'GET',
      url: `/operation/facility/short-name/available?${params.toString()}`,
    });

    return response ?? { isAvailable: true };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return handleError(error);
  }
};
